import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Upload, Download, AlertCircle } from 'lucide-react'
import { useToast } from '@/components/ui/use-toast'
import { services } from '@/lib/api/index'
import { DEFAULT_AVATAR } from '@/constants'
import type { CreateParentDTO } from '@/interface/types/user'
import { useTranslation } from 'react-i18next'

interface ParentImportData {
  firstname: string
  lastname: string
  email: string
  cin: string
  password: string
  gender: string
  birthday?: Date
  address?: string
  phone?: string
}

interface ImportResult {
  parent: ParentImportData
  success: boolean
  error?: string
  rowNumber: number
}

interface BulkImportStats {
  total: number
  successful: number
  failed: number
  results: ImportResult[]
}

interface BulkParentImportProps {
  onImportComplete?: () => void
}

export function BulkParentImport({ onImportComplete }: BulkParentImportProps) {
  const { t } = useTranslation('admin', { keyPrefix: 'parent-folder.bulkParentImport' })
  const [isOpen, setIsOpen] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [importing, setImporting] = useState(false)
  const [progress, setProgress] = useState(0)
  const [importStats, setImportStats] = useState<BulkImportStats | null>(null)
  const [showFailedDetails, setShowFailedDetails] = useState(false)
  const { toast } = useToast()

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile)
      setImportStats(null)
    } else {
      toast({
        title: t('notifications.invalidFile.title'),
        description: t('notifications.invalidFile.description'),
        variant: 'destructive'
      })
    }
  }

  const parseCSV = (csvText: string): ParentImportData[] => {
    const lines = csvText.split('\n').filter(line => line.trim())
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase())
    
    return lines.slice(1).map(line => {
      const values = line.split(',').map(v => v.trim())
      const parent: any = {}
      
      headers.forEach((header, index) => {
        const value = values[index] || ''
        switch (header) {
          case 'firstname':
          case 'first name':
            parent.firstname = value
            break
          case 'lastname':
          case 'last name':
            parent.lastname = value
            break
          case 'email':
            parent.email = value
            break
          case 'cin':
            parent.cin = value
            break
          case 'password':
            parent.password = value
            break
          case 'gender':
            parent.gender = value
            break
          case 'phone':
            parent.phone = value
            break
          case 'address':
            parent.address = value
            break
          case 'birthday':
          case 'birthdate':
            if (value) {
              const dateParts = value.split('/')
              if (dateParts.length === 3) {
                const day = parseInt(dateParts[0])
                const month = parseInt(dateParts[1]) - 1
                const year = parseInt(dateParts[2])
                parent.birthday = new Date(year, month, day)
              }
            }
            break
        }
      })
      
      return parent as ParentImportData
    })
  }

  const validateParent = (parent: ParentImportData): string | null => {
    if (!parent.firstname || parent.firstname.length < 2) {
      return t('validationErrors.firstname')
    }
    if (!parent.lastname || parent.lastname.length < 2) {
      return t('validationErrors.lastname')
    }
    if (!parent.email || !/\S+@\S+\.\S+/.test(parent.email)) {
      return t('validationErrors.email')
    }
    if (!parent.cin || parent.cin.length < 8) {
      return t('validationErrors.cin')
    }
    if (!parent.password || parent.password.length < 6) {
      return t('validationErrors.password')
    }
    if (!parent.gender || !['male', 'female'].includes(parent.gender.toLowerCase())) {
      return t('validationErrors.gender')
    }
    
    if (parent.birthday) {
      const today = new Date()
      const birthDate = new Date(parent.birthday)
      let age = today.getFullYear() - birthDate.getFullYear()
      const monthDiff = today.getMonth() - birthDate.getMonth()
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--
      }
      
      if (age < 18) {
        return t('validationErrors.age')
      }
    }
    
    return null
  }

  const handleImport = async () => {
    if (!file) return

    setImporting(true)
    setProgress(0)
    setImportStats(null)

    try {
      const csvText = await file.text()
      const parents = parseCSV(csvText)
      const results: ImportResult[] = []
      
      for (let i = 0; i < parents.length; i++) {
        const parent = parents[i]
        const rowNumber = i + 2
        
        const validationError = validateParent(parent)
        if (validationError) {
          results.push({
            parent,
            success: false,
            error: validationError,
            rowNumber
          })
          setProgress(((i + 1) / parents.length) * 100)
          continue
        }

        try {
          const defaultBirthday = new Date()
          defaultBirthday.setFullYear(defaultBirthday.getFullYear() - 35)

          const createData: CreateParentDTO = {
            firstname: parent.firstname,
            lastname: parent.lastname,
            email: parent.email,
            password: parent.password,
            cin: parent.cin,
            birthday: parent.birthday ? parent.birthday.toISOString() : defaultBirthday.toISOString(),
            gender: parent.gender.toLowerCase() as 'male' | 'female',
            avatar: DEFAULT_AVATAR,
            isActive: true,
            studentIds: [],
            address: parent.address,
            phone: parent.phone,
          }

       // console.logCreating parent with data:', createData)
        await services.parent.create(createData)
       // console.logParent created successfully:', response)
          results.push({
            parent,
            success: true,
            rowNumber
          })
        } catch (error: any) {
          // console.error('Error creating parent:', error)
          // console.error('Error response:', error.response)
          
          let errorMessage = 'Unknown error occurred'
          
          if (error.response?.data) {
            const apiError = error.response.data
            
         // console.logFull API error:', JSON.stringify(apiError, null, 2))
            
            if (typeof apiError.message === 'string') {
              errorMessage = apiError.message
            } else if (Array.isArray(apiError.message)) {
              const errorMessages = apiError.message.map((err: any) => {
                if (typeof err === 'string') return err
                if (err.field && err.message) return `${err.field}: ${err.message}`
                return JSON.stringify(err)
              })
              errorMessage = errorMessages.join(', ')
            } else if (apiError.message && typeof apiError.message === 'object') {
              const validationErrors = []
              if (apiError.message.email) {
                validationErrors.push('Email already exists')
              }
              if (apiError.message.cin) {
                validationErrors.push('CIN already exists')
              }
              if (apiError.message.birthday) {
                validationErrors.push('Invalid birthday or age requirement not met')
              }
              errorMessage = validationErrors.length > 0 ? validationErrors.join(', ') : 'Validation error'
            } else if (apiError.error === 'CONFLICT') {
              errorMessage = 'Data conflict - Email or CIN already exists'
            } else if (apiError.error === 'BAD_REQUEST') {
              errorMessage = 'Invalid data provided'
            } else {
              errorMessage = apiError.error || 'Server error'
            }
          } else if (error.message) {
            errorMessage = error.message
          }
          
          results.push({
            parent,
            success: false,
            error: errorMessage,
            rowNumber
          })
        }
        
        setProgress(((i + 1) / parents.length) * 100)
      }

      const stats: BulkImportStats = {
        total: parents.length,
        successful: results.filter(r => r.success).length,
        failed: results.filter(r => !r.success).length,
        results
      }

      setImportStats(stats)
      
      toast({
        title: t('notifications.complete.title'),
        description: t('notifications.complete.description', { successful: stats.successful, total: stats.total })
      })

      if (onImportComplete && stats.successful > 0) {
        onImportComplete()
      }

    } catch (error) {
      // console.error('CSV processing error:', error)
      toast({
        title: t('notifications.failed.title'),
        description: t('notifications.failed.description'),
        variant: 'destructive'
      })
    } finally {
      setImporting(false)
    }
  }

  const downloadTemplate = () => {
    const template = `firstname,lastname,email,cin,password,gender,phone,address,birthday\nJohn,Smith,john.smith@example.com,12345678,Password123!,Male,+1234567890,123 Main St,15/05/1980`
    const blob = new Blob([template], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'parent_import_template.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <>
      <Button onClick={() => setIsOpen(true)} className="gap-2">
        <Upload className="h-4 w-4" />
        {t('buttons.open')}
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('title')}</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t('alerts.instructions')}
                <Button variant="link" onClick={downloadTemplate} className="p-0 ml-2">
                  <Download className="h-4 w-4 mr-1" />
                  {t('buttons.downloadTemplate')}
                </Button>
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="csv-file">{t('labels.csvFile')}</Label>
              <Input
                id="csv-file"
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                disabled={importing}
              />
            </div>

            {importing && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>{t('labels.importing')}</span>
                  <span>{t('labels.progress', { progress: Math.round(progress) })}</span>
                </div>
                <Progress value={progress} />
              </div>
            )}

            {importStats && (
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Badge variant="outline" className="text-green-600">
                    {t('badges.successful', { count: importStats.successful })}
                  </Badge>
                  <Badge variant="outline" className="text-red-600">
                    {t('badges.failed', { count: importStats.failed })}
                  </Badge>
                </div>

                {importStats.failed > 0 && (
                  <Button 
                    variant="outline" 
                    onClick={() => setShowFailedDetails(!showFailedDetails)}
                    className="w-full"
                  >
                    {showFailedDetails ? t('buttons.hideFailed') : t('buttons.showFailed')}
                  </Button>
                )}

                {showFailedDetails && importStats.failed > 0 && (
                  <div className="max-h-60 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('table.headers.row')}</TableHead>
                          <TableHead>{t('table.headers.name')}</TableHead>
                          <TableHead>{t('table.headers.email')}</TableHead>
                          <TableHead>{t('table.headers.error')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {importStats.results
                          .filter(result => !result.success)
                          .map((result, index) => (
                            <TableRow key={index}>
                              <TableCell>{result.rowNumber}</TableCell>
                              <TableCell>
                                {result.parent.firstname} {result.parent.lastname}
                              </TableCell>
                              <TableCell>{result.parent.email}</TableCell>
                              <TableCell>
                                <Badge variant="destructive" className="text-xs">
                                  {result.error}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsOpen(false)}>
                {t('buttons.close')}
              </Button>
              <Button 
                onClick={handleImport} 
                disabled={!file || importing}
                className="gap-2"
              >
                {importing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    {t('labels.importing')}
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    {t('buttons.import')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
